import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { DateTime } from 'luxon';
import { JobboardAdapter } from '../../adapters/jobboard.adapter';
import { Fields } from '../../interfaces/fields';
import { Job } from '../../interfaces/job';
import { JobDetail } from '../../interfaces/job-detail';
import { PlatformApplicationSource } from '../enums/platform-application-source.enum';
import { PlatformApplicationStatus } from '../enums/platform-application-status.enum';
import { PlatformApplyWithProfileKind } from '../enums/platform-apply-with-profile-kind.enum';
import { PlatformApplyWithProfileMode } from '../enums/platform-apply-with-profile-mode.enum';
import { PlatformInvitationType } from '../enums/platform-invitation-type.enum';
import { PlatformMissingDataType } from '../enums/platform-missing-data-type.enum';
import {
  PlatformActionItem,
  PlatformApiInvitation,
  PlatformApiRecommendation,
  PlatformApiSavedJob,
  PlatformApplication,
  PlatformApplyWithProfilePayload,
  PlatformBoardJob,
  PlatformFollowUpPayload,
  PlatformInvitation,
  PlatformJobRequirements,
  PlatformSavedJob,
  formatPlatformSalary,
  resolveJobRequirements,
  toPlatformBoardJob,
} from '../models/platform.models';
import { PlatformAuthService } from './platform-auth.service';
import { PlatformCandidateApiService } from './platform-candidate-api.service';
import { PlatformProfileService } from './platform-profile.service';
import { PlatformSettingsService } from './platform-settings.service';

export interface PlatformJobboardBookmarkPayload {
  jobId: number;
  companyId: number;
  jobTitle: string;
  companyName: string;
  location: string;
  salary: string;
  jobUrl: string;
  closed: boolean;
}

export interface PlatformApplyWithProfileContext {
  mode: PlatformApplyWithProfileMode;
  kind: PlatformApplyWithProfileKind;
  jobTitle: string;
  companyName: string;
  location: string;
  jobUrl: string | null;
  source: PlatformApplicationSource;
  jobId?: number;
  companyId?: number;
  savedJobId?: number;
  invitationId?: number;
  applicationId?: number;
  requirements: PlatformJobRequirements;
  requiredSkills: string[];
  manualApplyUrl: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PlatformJobsService {
  private readonly auth = inject(PlatformAuthService);
  private readonly profile = inject(PlatformProfileService);
  private readonly settings = inject(PlatformSettingsService);
  private readonly api = inject(PlatformCandidateApiService);
  private readonly jobboard = inject(JobboardAdapter);

  private readonly applicationsState = signal<PlatformApplication[]>([]);
  private readonly savedJobsState = signal<PlatformSavedJob[]>([]);
  private readonly invitationsState = signal<PlatformInvitation[]>([]);
  private readonly listedJobsState = signal<PlatformBoardJob[]>([]);
  private readonly recommendedJobsState = signal<PlatformBoardJob[]>([]);
  private readonly extraBoardJobsState = signal<PlatformBoardJob[]>([]);
  private loadedFor: number | null = null;

  constructor() {
    effect(() => {
      const me = this.auth.session();
      untracked(() => {
        if (!me) {
          this.loadedFor = null;
          this.applicationsState.set([]);
          this.savedJobsState.set([]);
          this.invitationsState.set([]);
          this.listedJobsState.set([]);
          this.recommendedJobsState.set([]);
          this.extraBoardJobsState.set([]);
          return;
        }
        if (this.loadedFor === me.id) {
          return;
        }
        this.loadedFor = me.id;
        this.reloadAll();
      });
    });
  }

  readonly applications = this.applicationsState.asReadonly();
  readonly savedJobs = this.savedJobsState.asReadonly();
  readonly invitations = this.invitationsState.asReadonly();
  readonly listedJobs = computed(() =>
    this.mergeBoardFlags(this.listedJobsState()).filter((job) => job.listed && job.open)
  );
  readonly recommendedJobs = computed(() => {
    if (this.settings.recommendationsPaused()) {
      return [];
    }
    return this.recommendedJobsState().filter((job) => job.open);
  });

  readonly incompleteApplications = computed(() =>
    this.applications().filter(
      (application) => application.status === PlatformApplicationStatus.INCOMPLETE
    )
  );
  readonly inProgressApplications = computed(() =>
    this.applications().filter(
      (application) => application.status === PlatformApplicationStatus.IN_PROGRESS
    )
  );
  readonly processEndedApplications = computed(() =>
    this.applications().filter(
      (application) => application.status === PlatformApplicationStatus.PROCESS_ENDED
    )
  );
  readonly actionItems = computed((): PlatformActionItem[] =>
    this.applications()
      .filter((application) => this.hasActionItem(application))
      .map((application) => ({
        id: application.id,
        applicationId: application.id,
        jobTitle: application.jobTitle,
        companyId: application.companyId,
        companyName: application.companyName,
        missingData: application.missingData,
      }))
  );

  /**
   * Reloads tracked applications, saved jobs, invitations and listings.
   */
  reloadAll(): void {
    this.reloadRemoteApplications();
    this.reloadSavedJobs();
    this.reloadInvitations();
    this.reloadRecommendations();
    this.reloadListedJobs();
  }

  /**
   * Reloads tracked applications from the candidate account.
   */
  reloadRemoteApplications(): void {
    if (!this.auth.loggedIn()) {
      return;
    }
    this.api.applications().subscribe({
      next: (applications) => this.applicationsState.set(applications),
      error: () => this.applicationsState.set([]),
    });
  }

  /**
   * Whether an application currently has a follow-up action item.
   * @param application - Tracked application
   * @returns True when the applicant still needs to submit missing data
   */
  hasActionItem(application: PlatformApplication): boolean {
    return (
      application.jobAvailable &&
      application.status !== PlatformApplicationStatus.PROCESS_ENDED &&
      application.missingData.length > 0
    );
  }

  /**
   * Submits follow-up data for an incomplete application.
   * @param payload - Follow-up values from the complete-application modal
   * @param applicationId - Application id when known
   */
  completeFollowUp(payload: PlatformFollowUpPayload, applicationId?: number): void {
    if (applicationId == null) {
      return;
    }
    this.api.followUp(applicationId, this.followUpForm(payload)).subscribe({
      next: () => {
        this.reloadRemoteApplications();
        this.auth.refreshMe().subscribe();
      },
    });
  }

  /**
   * Removes an application from applicant tracking only.
   * @param applicationId - Tracked application id
   */
  removeApplicationFromTracking(applicationId: number): void {
    this.api.removeFromTracking(applicationId).subscribe({
      next: () => {
        this.applicationsState.update((applications) =>
          applications.filter((application) => application.id !== applicationId)
        );
      },
    });
  }

  /**
   * Whether a job is currently saved.
   * @param jobId - Public job id
   * @param companyId - Company id
   * @returns True when the job is bookmarked
   */
  isJobSaved(jobId: number, _companyId: number): boolean {
    return this.savedJobs().some((job) => job.jobId === jobId);
  }

  /**
   * Saves a job or removes it when it is already bookmarked.
   * @param payload - Job snapshot from browsing
   * @returns True when the job is saved afterwards
   */
  toggleSavedJob(payload: PlatformJobboardBookmarkPayload): boolean {
    const existing = this.savedJobs().find((job) => job.jobId === payload.jobId);
    if (existing) {
      this.unsaveJob(existing.id);
      return false;
    }
    this.savedJobsState.update((jobs) => [
      {
        id: payload.jobId,
        jobId: payload.jobId,
        companyId: payload.companyId,
        jobTitle: payload.jobTitle,
        companyName: payload.companyName,
        location: payload.location,
        salary: payload.salary,
        pinnedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        applied: this.hasAppliedToJobId(payload.jobId, payload.jobTitle, payload.companyName),
        closed: payload.closed,
        jobUrl: payload.jobUrl,
      },
      ...jobs,
    ]);
    this.api.saveJob(payload.jobId).subscribe({
      error: () => this.reloadSavedJobs(),
    });
    return true;
  }

  /**
   * Removes a saved job.
   * @param savedJobId - Saved job row id (same as job id)
   */
  unsaveJob(savedJobId: number): void {
    this.savedJobsState.update((jobs) => jobs.filter((job) => job.id !== savedJobId));
    this.api.unsaveJob(savedJobId).subscribe({
      error: () => this.reloadSavedJobs(),
    });
  }

  /**
   * Starts apply-with-profile from a saved job by applying to the job id.
   * @param savedJobId - Saved job row id
   */
  applyToSavedJob(savedJobId: number): void {
    const savedJob = this.savedJobs().find((job) => job.id === savedJobId);
    if (!savedJob || savedJob.closed) {
      return;
    }
    this.submitApplyWithProfile(this.applyContextFromSavedJob(savedJob), {
      skills: this.profile.profile().skills,
    });
  }

  /**
   * Accepts a hub invitation by applying to the job.
   * @param invitationId - Invitation id (job id)
   */
  acceptInvitation(invitationId: number): void {
    const invitation = this.invitations().find((item) => item.id === invitationId);
    if (!invitation || invitation.closed) {
      return;
    }
    this.submitApplyWithProfile(this.applyContextFromInvitation(invitation), {
      skills: this.profile.profile().skills,
    });
  }

  /**
   * Declines an invitation and hides the job from future suggestions.
   * @param invitationId - Invitation id (job id)
   */
  declineInvitation(invitationId: number): void {
    this.api.declineInvitation(invitationId).subscribe({
      next: () => {
        this.invitationsState.update((invitations) =>
          invitations.filter((invitation) => invitation.id !== invitationId)
        );
        this.reloadRecommendations();
      },
    });
  }

  /**
   * Removes a closed invitation.
   * @param invitationId - Invitation id (job id)
   */
  removeInvitation(invitationId: number): void {
    this.api.removeInvitation(invitationId).subscribe({
      next: () => {
        this.invitationsState.update((invitations) =>
          invitations.filter((invitation) => invitation.id !== invitationId)
        );
      },
    });
  }

  /**
   * Builds a bookmark payload from a public jobboard job.
   * @param job - Job listing or detail
   * @returns Bookmark payload
   */
  bookmarkPayloadFromJob(job: {
    id: number;
    companyID: number;
    companyTitle: string;
    jobTitle: string;
    workLocations?: Array<{ city?: string }>;
    searchRegionAddress?: { city?: string } | null;
    salaryMin?: number | null;
    salaryMax?: number | null;
    salary?: number | null;
    currency?: string | null;
    open?: boolean;
  }): PlatformJobboardBookmarkPayload {
    const location =
      job.workLocations?.find((item) => item.city)?.city ??
      job.searchRegionAddress?.city ??
      '';
    return {
      jobId: job.id,
      companyId: job.companyID,
      jobTitle: job.jobTitle,
      companyName: job.companyTitle,
      location,
      salary: formatPlatformSalary(job.salaryMin ?? job.salary, job.salaryMax, job.currency),
      jobUrl: `/companies/${job.companyID}/jobs/${job.id}`,
      closed: job.open === false,
    };
  }

  /**
   * Builds a bookmark payload from a board job.
   * @param job - Board job
   * @returns Bookmark payload
   */
  bookmarkPayloadFromBoardJob(job: PlatformBoardJob): PlatformJobboardBookmarkPayload {
    return {
      jobId: job.id,
      companyId: job.companyId,
      jobTitle: job.jobTitle,
      companyName: job.companyName,
      location: job.location,
      salary: job.salary,
      jobUrl: this.boardJobUrl(job.id),
      closed: !job.open,
    };
  }

  /**
   * Returns a board job by id from loaded listings.
   * @param jobId - Board job id
   * @returns Job or undefined
   */
  boardJobById(jobId: number): PlatformBoardJob | undefined {
    return this.allBoardJobs().find((job) => job.id === jobId);
  }

  /**
   * Loads a public job into the board-job cache when it is not listed yet.
   * @param jobId - Public job id
   */
  loadBoardJob(jobId: number): void {
    if (!Number.isFinite(jobId) || this.boardJobById(jobId)) {
      return;
    }
    this.jobboard.getJob(jobId).subscribe({
      next: (job) => {
        const boardJob = this.boardJobFromDetail(job);
        this.extraBoardJobsState.update((jobs) => [
          boardJob,
          ...jobs.filter((item) => item.id !== boardJob.id),
        ]);
      },
    });
  }

  /**
   * In-platform URL for a board job.
   * @param jobId - Board job id
   * @returns Platform route
   */
  boardJobUrl(jobId: number): string {
    return `/jobs/recommendations/${jobId}`;
  }

  /**
   * Public jobboard URL for a job.
   * @param companyId - Company id
   * @param jobId - Job id
   * @returns Public job path or null
   */
  jobboardJobUrl(
    companyId: number | null | undefined,
    jobId: number | null | undefined
  ): string | null {
    if (companyId == null || jobId == null) {
      return null;
    }
    return `/companies/${companyId}/jobs/${jobId}`;
  }

  /**
   * Public company jobboard URL.
   * @param companyId - Company id
   * @returns Company jobboard path or null
   */
  jobboardCompanyUrl(companyId: number | null | undefined): string | null {
    if (companyId == null) {
      return null;
    }
    return `/companies/${companyId}`;
  }

  /**
   * Whether the candidate already applied to this board job.
   * @param job - Board job
   * @returns True when an application exists
   */
  hasAppliedToBoardJob(job: PlatformBoardJob): boolean {
    return this.hasAppliedToJobId(job.id, job.jobTitle, job.companyName);
  }

  /**
   * Applies to a board job with the current profile.
   * @param jobId - Board job id
   */
  applyToBoardJob(jobId: number): void {
    const job = this.boardJobById(jobId);
    if (!job || !job.open || this.hasAppliedToBoardJob(job)) {
      return;
    }
    this.submitApplyWithProfile(this.applyContextFromBoardJob(job), {
      skills: this.profile.profile().skills,
    });
  }

  /**
   * Builds the apply-with-profile context for a board job.
   * @param job - Board job
   * @returns Modal context
   */
  applyContextFromBoardJob(job: PlatformBoardJob): PlatformApplyWithProfileContext {
    return {
      mode: PlatformApplyWithProfileMode.APPLY,
      kind: PlatformApplyWithProfileKind.BOARD,
      jobTitle: job.jobTitle,
      companyName: job.companyName,
      location: job.location,
      jobUrl: this.boardJobUrl(job.id),
      source:
        job.invited || this.hasInvitationForJob(job.jobTitle, job.companyName)
          ? PlatformApplicationSource.TALENTHUB
          : PlatformApplicationSource.QUICK_APPLY,
      jobId: job.id,
      companyId: job.companyId,
      requirements: resolveJobRequirements(),
      requiredSkills: [...job.skills],
      manualApplyUrl: `/jobs/recommendations/${job.id}/apply`,
    };
  }

  /**
   * Builds the apply-with-profile context for a saved job.
   * @param savedJob - Saved job
   * @returns Modal context
   */
  applyContextFromSavedJob(savedJob: PlatformSavedJob): PlatformApplyWithProfileContext {
    const boardJob = this.boardJobById(savedJob.jobId);
    if (boardJob) {
      return {
        ...this.applyContextFromBoardJob(boardJob),
        kind: PlatformApplyWithProfileKind.SAVED,
        savedJobId: savedJob.id,
        jobUrl: savedJob.jobUrl,
      };
    }
    return {
      mode: PlatformApplyWithProfileMode.APPLY,
      kind: PlatformApplyWithProfileKind.SAVED,
      jobTitle: savedJob.jobTitle,
      companyName: savedJob.companyName,
      location: savedJob.location,
      jobUrl: savedJob.jobUrl,
      source: this.hasInvitationForJob(savedJob.jobTitle, savedJob.companyName)
        ? PlatformApplicationSource.TALENTHUB
        : PlatformApplicationSource.QUICK_APPLY,
      savedJobId: savedJob.id,
      jobId: savedJob.jobId,
      companyId: savedJob.companyId,
      requirements: resolveJobRequirements(),
      requiredSkills: [],
      manualApplyUrl: savedJob.jobUrl,
    };
  }

  /**
   * Builds the apply-with-profile context for an invitation.
   * @param invitation - Invitation
   * @returns Modal context
   */
  applyContextFromInvitation(invitation: PlatformInvitation): PlatformApplyWithProfileContext {
    const boardJob = this.boardJobById(invitation.jobId);
    if (boardJob) {
      return {
        ...this.applyContextFromBoardJob(boardJob),
        kind: PlatformApplyWithProfileKind.INVITATION,
        invitationId: invitation.id,
        source: PlatformApplicationSource.TALENTHUB,
      };
    }
    return {
      mode: PlatformApplyWithProfileMode.APPLY,
      kind: PlatformApplyWithProfileKind.INVITATION,
      jobTitle: invitation.jobTitle,
      companyName: invitation.companyName,
      location: invitation.location,
      jobUrl: invitation.jobUrl,
      source: PlatformApplicationSource.TALENTHUB,
      invitationId: invitation.id,
      jobId: invitation.jobId,
      companyId: invitation.companyId,
      requirements: resolveJobRequirements(),
      requiredSkills: [],
      manualApplyUrl: this.manualApplyUrlFromJobUrl(invitation.jobUrl),
    };
  }

  /**
   * Builds the apply-with-profile context for a public jobboard job.
   * @param job - Public job detail
   * @returns Modal context
   */
  applyContextFromJobboardJob(job: JobDetail): PlatformApplyWithProfileContext {
    return {
      mode: PlatformApplyWithProfileMode.APPLY,
      kind: PlatformApplyWithProfileKind.JOBBOARD,
      jobTitle: job.jobTitle,
      companyName: job.companyTitle,
      location:
        job.workLocations?.find((item) => item.city)?.city ??
        job.searchRegionAddress?.city ??
        '',
      jobUrl: `/companies/${job.companyID}/jobs/${job.id}`,
      source: PlatformApplicationSource.QUICK_APPLY,
      jobId: job.id,
      companyId: job.companyID,
      requirements: this.requirementsFromFields(job.fields),
      requiredSkills: (job.skills ?? []).map((skill) => skill.name),
      manualApplyUrl: `/companies/${job.companyID}/jobs/${job.id}/apply`,
    };
  }

  /**
   * Builds a follow-up context from a public job, reusing a matching incomplete application when present.
   * @param job - Public job detail
   * @returns Follow-up modal context
   */
  followUpContextFromJobboardJob(job: JobDetail): PlatformApplyWithProfileContext {
    const existing = this.applications().find(
      (application) =>
        application.jobTitle === job.jobTitle &&
        application.companyName === job.companyTitle &&
        application.status === PlatformApplicationStatus.INCOMPLETE
    );
    if (existing) {
      return this.followUpContextFromApplication(existing);
    }
    return {
      ...this.applyContextFromJobboardJob(job),
      mode: PlatformApplyWithProfileMode.FOLLOW_UP,
      kind: PlatformApplyWithProfileKind.FOLLOW_UP,
      manualApplyUrl: null,
    };
  }

  /**
   * Builds the follow-up apply-with-profile context for an incomplete application.
   * @param application - Tracked application
   * @returns Modal context
   */
  followUpContextFromApplication(
    application: PlatformApplication
  ): PlatformApplyWithProfileContext {
    const jobId = this.jobIdFromUrl(application.jobUrl);
    const boardJob = jobId != null ? this.boardJobById(jobId) : undefined;
    return {
      mode: PlatformApplyWithProfileMode.FOLLOW_UP,
      kind: PlatformApplyWithProfileKind.FOLLOW_UP,
      jobTitle: application.jobTitle,
      companyName: application.companyName,
      location: application.location,
      jobUrl: application.jobUrl,
      source: application.source,
      applicationId: application.id,
      jobId,
      requirements: this.requirementsFromMissingData(application.missingData),
      requiredSkills: boardJob?.skills ?? [],
      manualApplyUrl: null,
    };
  }

  /**
   * Whether the candidate already applied to this public jobboard job.
   * @param job - Public job identity
   * @returns True when an application exists
   */
  hasAppliedToJobboardJob(job: {
    id?: number;
    jobTitle: string;
    companyTitle: string;
  }): boolean {
    return this.hasAppliedToJobId(job.id, job.jobTitle, job.companyTitle);
  }

  /**
   * Records an application from a public jobboard job via the profile apply API.
   * @param job - Public job detail
   */
  applyToJobboardJob(job: JobDetail): void {
    if (this.hasAppliedToJobboardJob(job)) {
      return;
    }
    this.submitApplyWithProfile(this.applyContextFromJobboardJob(job), {
      skills: this.profile.profile().skills,
    });
  }

  /**
   * Submits apply-with-profile data and creates or completes the application.
   * @param context - Modal context
   * @param payload - Submitted profile values
   */
  submitApplyWithProfile(
    context: PlatformApplyWithProfileContext,
    payload: PlatformApplyWithProfilePayload
  ): void {
    if (context.mode === PlatformApplyWithProfileMode.FOLLOW_UP) {
      this.completeFollowUp(
        {
          salary: payload.salary,
          startDate: payload.startDate,
          skills: payload.skills,
          cvFile: payload.cvFile,
          certificateFiles: payload.certificateFiles,
        },
        context.applicationId
      );
      return;
    }
    if (context.jobId == null) {
      return;
    }
    const form = this.applyForm(payload, context);
    this.api.applyWithProfile(context.jobId, form).subscribe({
      next: () => this.reloadAll(),
    });
  }

  /**
   * Returns the transloco key for an application status.
   * @param status - Application tracking status
   * @returns Translation key
   */
  statusTranslationKey(status: PlatformApplicationStatus): string {
    return `jobboard.platform.jobs.applications.status.${status}`;
  }

  /**
   * Returns the transloco key for a missing-data type.
   * @param type - Missing data type
   * @returns Translation key
   */
  missingDataTranslationKey(type: string): string {
    return `jobboard.platform.dashboard.action-items.missing.${type}`;
  }

  /**
   * Reloads saved jobs from the candidate account.
   */
  private reloadSavedJobs(): void {
    this.api.savedJobs().subscribe({
      next: (jobs) => this.savedJobsState.set(jobs.map((job) => this.toSavedJob(job))),
      error: () => this.savedJobsState.set([]),
    });
  }

  /**
   * Reloads open Talenthub invitations.
   */
  private reloadInvitations(): void {
    this.api.invitations().subscribe({
      next: (invitations) =>
        this.invitationsState.set(invitations.map((invitation) => this.toInvitation(invitation))),
      error: () => this.invitationsState.set([]),
    });
  }

  /**
   * Reloads recommended jobs.
   */
  private reloadRecommendations(): void {
    this.api.recommendations().subscribe({
      next: (recommendations) =>
        this.recommendedJobsState.set(
          recommendations.map((item) => this.toRecommendedJob(item))
        ),
      error: () => this.recommendedJobsState.set([]),
    });
  }

  /**
   * Reloads publicly listed jobs.
   */
  private reloadListedJobs(): void {
    this.jobboard.getJobs({ offset: 0, limit: 100 }).subscribe({
      next: (response) =>
        this.listedJobsState.set(response.results.map((job) => this.toBoardJobFromPublic(job))),
      error: () => this.listedJobsState.set([]),
    });
  }

  /**
   * Maps a saved-job API row to the UI model.
   * @param item - API saved job
   * @returns Saved job
   */
  private toSavedJob(item: PlatformApiSavedJob): PlatformSavedJob {
    return {
      id: item.job.id,
      jobId: item.job.id,
      companyId: item.job.companyId,
      jobTitle: item.job.jobTitle,
      companyName: item.job.companyName,
      location: item.job.location,
      salary: formatPlatformSalary(item.job.salaryMin, item.job.salaryMax, item.job.currency),
      pinnedAt: item.savedAt,
      updatedAt: item.job.updatedAt,
      applied: item.applied,
      closed: item.job.availability !== 'open',
      jobUrl: item.job.jobUrl,
    };
  }

  /**
   * Maps an invitation API row to the UI model.
   * @param item - API invitation
   * @returns Invitation
   */
  private toInvitation(item: PlatformApiInvitation): PlatformInvitation {
    return {
      id: item.job.id,
      jobId: item.job.id,
      companyId: item.job.companyId,
      type: PlatformInvitationType.TALENTHUB,
      companyName: item.job.companyName,
      jobTitle: item.job.jobTitle,
      location: item.job.location,
      salary: formatPlatformSalary(item.job.salaryMin, item.job.salaryMax, item.job.currency),
      receivedAt: item.receivedAt,
      closed: item.job.availability !== 'open',
      jobUrl: item.job.jobUrl,
      score: item.score,
    };
  }

  /**
   * Maps a recommendation API row to a board job.
   * @param item - API recommendation
   * @returns Board job
   */
  private toRecommendedJob(item: PlatformApiRecommendation): PlatformBoardJob {
    return toPlatformBoardJob(item.job, {
      recommended: true,
      invited: item.invited,
      score: item.score,
    });
  }

  /**
   * Maps a public jobboard job to a board job.
   * @param job - Public job
   * @returns Board job
   */
  private toBoardJobFromPublic(job: Job): PlatformBoardJob {
    const location =
      job.workLocations?.find((item) => item.city)?.city ??
      job.searchRegionAddress?.city ??
      job.searchRegion ??
      '';
    return {
      id: job.id,
      companyId: job.companyID,
      jobTitle: job.jobTitle,
      companyName: job.companyTitle,
      location,
      salary: formatPlatformSalary(job.salaryMin, job.salaryMax, job.currency),
      salaryMin: job.salaryMin ?? null,
      salaryMax: job.salaryMax ?? null,
      workingModel: job.workingModel,
      employmentType: job.employmentType,
      workingTime: job.workingTime,
      postedAt: '',
      updatedAt: '',
      recommended: false,
      invited: false,
      score: null,
      listed: true,
      open: true,
      skills: [],
    };
  }

  /**
   * Maps a public job detail to a board job.
   * @param job - Job detail
   * @returns Board job
   */
  private boardJobFromDetail(job: JobDetail): PlatformBoardJob {
    const location =
      job.workLocations?.find((item) => item.city)?.city ??
      job.searchRegionAddress?.city ??
      '';
    return {
      id: job.id,
      companyId: job.companyID,
      jobTitle: job.jobTitle,
      companyName: job.companyTitle,
      location,
      salary: formatPlatformSalary(job.salaryMin, job.salaryMax, job.currency),
      salaryMin: job.salaryMin ?? null,
      salaryMax: job.salaryMax ?? null,
      workingModel: job.workingModel,
      employmentType: job.employmentType,
      workingTime: job.workingTime,
      postedAt: job.postingTime,
      updatedAt: job.postingTime,
      recommended: false,
      invited: false,
      score: null,
      listed: true,
      open: job.open !== false,
      skills: (job.skills ?? []).map((skill) => skill.name),
    };
  }

  /**
   * Copies recommendation flags onto listed jobs.
   * @param jobs - Listed jobs
   * @returns Jobs with recommendation flags
   */
  private mergeBoardFlags(jobs: PlatformBoardJob[]): PlatformBoardJob[] {
    const recommended = new Map(this.recommendedJobsState().map((job) => [job.id, job]));
    const invited = new Set(this.invitations().map((invitation) => invitation.jobId));
    return jobs.map((job) => {
      const reco = recommended.get(job.id);
      return {
        ...job,
        recommended: reco != null,
        invited: reco?.invited === true || invited.has(job.id),
        score: reco?.score ?? job.score,
        skills: reco?.skills?.length ? reco.skills : job.skills,
      };
    });
  }

  /**
   * All loaded board jobs including extras fetched for detail views.
   * @returns Board jobs
   */
  private allBoardJobs(): PlatformBoardJob[] {
    const byId = new Map<number, PlatformBoardJob>();
    for (const job of [
      ...this.listedJobsState(),
      ...this.recommendedJobsState(),
      ...this.extraBoardJobsState(),
    ]) {
      byId.set(job.id, job);
    }
    return [...byId.values()];
  }

  /**
   * Whether tracking already contains this job.
   * @param jobId - Optional job id
   * @param jobTitle - Job title
   * @param companyName - Company name
   * @returns True when an application exists
   */
  private hasAppliedToJobId(
    jobId: number | undefined,
    jobTitle: string,
    companyName: string
  ): boolean {
    return this.applications().some((application) => {
      if (jobId != null && this.jobIdFromUrl(application.jobUrl) === jobId) {
        return true;
      }
      return application.jobTitle === jobTitle && application.companyName === companyName;
    });
  }

  /**
   * Whether an open invitation exists for this job.
   * @param jobTitle - Job title
   * @param companyName - Company name
   * @returns True when a matching invitation is present
   */
  private hasInvitationForJob(jobTitle: string, companyName: string): boolean {
    return this.invitations().some(
      (invitation) =>
        invitation.jobTitle === jobTitle && invitation.companyName === companyName
    );
  }

  /**
   * Maps public job fields to required-field flags.
   * @param fields - Job application form fields
   * @returns Required fields for the modal
   */
  private requirementsFromFields(fields?: Fields | null): PlatformJobRequirements {
    return resolveJobRequirements({
      phone: fields?.phone?.required === true,
      startDate: fields?.startDate?.required === true,
      salary: fields?.salary?.required === true,
      cv: fields?.cv?.required !== false,
      coverLetter: fields?.coverLetter?.required === true,
      certificates: fields?.certificates?.required === true,
    });
  }

  /**
   * Maps remaining missing-data types to required-field flags.
   * @param missing - Missing data types on the application
   * @returns Required fields for the follow-up modal
   */
  private requirementsFromMissingData(
    missing: PlatformMissingDataType[]
  ): PlatformJobRequirements {
    return resolveJobRequirements({
      phone: false,
      startDate: missing.includes(PlatformMissingDataType.START_DATE),
      salary: missing.includes(PlatformMissingDataType.SALARY),
      cv: missing.includes(PlatformMissingDataType.DOCUMENTS),
      coverLetter: false,
      certificates: missing.includes(PlatformMissingDataType.DOCUMENTS),
    });
  }

  /**
   * Reads a job id from a platform or public job URL.
   * @param jobUrl - Job URL
   * @returns Job id or undefined
   */
  private jobIdFromUrl(jobUrl: string | null): number | undefined {
    if (!jobUrl) {
      return undefined;
    }
    const match = jobUrl.match(/\/jobs\/(?:recommendations\/)?(\d+)/);
    if (!match) {
      return undefined;
    }
    const jobId = Number(match[1]);
    return Number.isFinite(jobId) ? jobId : undefined;
  }

  /**
   * Builds a manual-apply URL from a job URL when possible.
   * @param jobUrl - Job URL
   * @returns Manual apply URL or null
   */
  private manualApplyUrlFromJobUrl(jobUrl: string | null): string | null {
    const jobId = this.jobIdFromUrl(jobUrl);
    if (jobId == null) {
      return jobUrl && jobUrl !== '/' ? jobUrl : null;
    }
    return `/jobs/recommendations/${jobId}/apply`;
  }

  /**
   * Builds the multipart payload for apply-with-profile.
   * @param payload - Submitted values
   * @param context - Modal context
   * @returns Form data
   */
  private applyForm(
    payload: PlatformApplyWithProfilePayload,
    context: PlatformApplyWithProfileContext
  ): FormData {
    const form = new FormData();
    if (payload.salary != null) {
      form.append('salary', String(payload.salary));
    }
    if (payload.startDate != null) {
      const iso = DateTime.fromSeconds(payload.startDate).toISODate();
      if (iso) {
        form.append('startDate', iso);
      }
    }
    if (
      context.kind === PlatformApplyWithProfileKind.INVITATION ||
      context.source === PlatformApplicationSource.TALENTHUB
    ) {
      form.append('acceptInvitation', 'true');
    }
    if (payload.cvFile instanceof File) {
      form.append('cv', payload.cvFile, payload.cvFile.name);
    }
    if (payload.coverLetterFile instanceof File) {
      form.append('coverLetter', payload.coverLetterFile, payload.coverLetterFile.name);
    }
    for (const file of payload.certificateFiles ?? []) {
      if (file instanceof File) {
        form.append('others[]', file, file.name);
      }
    }
    return form;
  }

  /**
   * Builds the multipart payload for a follow-up.
   * @param payload - Follow-up values
   * @returns Form data
   */
  private followUpForm(payload: PlatformFollowUpPayload): FormData {
    const form = new FormData();
    form.append('nonce', 'pending');
    if (payload.salary != null) {
      form.append('salary', String(payload.salary));
    }
    if (payload.startDate != null) {
      form.append('startDate', String(payload.startDate));
    }
    if (payload.skills && payload.skills.length > 0) {
      form.append('skills', JSON.stringify(payload.skills));
    }
    if (payload.isaAnswers && payload.isaAnswers.length > 0) {
      form.append('isa', JSON.stringify(payload.isaAnswers));
    } else if (payload.isaCompleted) {
      form.append('isa', JSON.stringify([]));
    }
    if (payload.cvFile instanceof File) {
      form.append('cv', payload.cvFile, payload.cvFile.name);
    }
    for (const file of payload.certificateFiles ?? []) {
      if (file instanceof File) {
        form.append('certificates[]', file, file.name);
      }
    }
    return form;
  }
}
